// pricehistory.ts — the catalog's price history (docs/SERVICE_SWITCHER_PLAN.md
// §13 item 26; the §13 item 18 follow-up).
//
// A recipe component's cost is what the margin on every concrete estimate is
// figured on. It moves three ways — the owner edits it on the rate card, the
// job cost sheet writes it onto the coat card, or a supplier's price list is
// imported — and until now the card only kept the current number. This keeps
// the trail: settings.priceHistory, newest first, one entry per component
// whose $/unit or pack price changed, who or what changed it, and from which
// file. The rate card editor shows the card's trail, and the import preview
// shows each row's last change so a second sheet reads against the first.
//
// Pure and unit-tested (scripts/unit-pricehistory.mjs). Both sides of a diff
// are read through componentsOf / systemComponentsOf, so a first-release card
// (flat assumptions) compares like a recipe and never reads as "all new".

import { componentsOf, systemComponentsOf } from "./engine.concrete";
import type { RateCard, Component } from "./engine.concrete";

export type PriceSource = "editor" | "import" | "jobcost";
export type PriceEntry = {
  at: number;                   // when the change landed
  jobType: string;              // the card
  systemId: string | null;      // a coat finish's own component, else null
  id?: string;                  // the component's id when it has one
  desc: string;                 // the component as named then
  sku?: string;
  unit?: string;                // the $/unit's unit (lb, gal, ea…)
  from: number | null;          // $/unit before; null = a new line
  to: number | null;            // $/unit after; null = the line was removed
  packFrom?: number | null;     // the pack price, when it moved
  packTo?: number | null;
  pct: number | null;           // the move in % of the old $/unit
  source: PriceSource;
  file?: string;                // the imported sheet
};

export const PRICE_HISTORY_CAP = 400;

const num = (v: any): number | null => (v == null || v === "" || !isFinite(Number(v)) ? null : Number(v));
const r2 = (n: number) => Math.round(n * 100) / 100;
const key = (s: any) => String(s || "").trim().toLowerCase().replace(/\s+/g, " ");

type Slot = { systemId: string | null; comp: Component };
function slotsOf(card: RateCard | null | undefined): Slot[] {
  if (!card || typeof card !== "object") return [];
  const out: Slot[] = [];
  componentsOf(card).forEach((comp) => { if (comp && comp.kind !== "labor") out.push({ systemId: null, comp }); });
  (card.systems || []).forEach((sys) => {
    const sid = String((sys && (sys.id || sys.name)) || "");
    systemComponentsOf(sys).forEach((comp) => { if (comp && comp.kind !== "labor") out.push({ systemId: sid, comp }); });
  });
  return out;
}

/* the component on the other side: by id first, then by name within the same list */
function pair(prev: Slot[], next: Slot[]): { a: Slot | null; b: Slot | null }[] {
  const out: { a: Slot | null; b: Slot | null }[] = [];
  const used = new Set<number>();
  next.forEach((b) => {
    let i = b.comp.id ? prev.findIndex((a, k) => !used.has(k) && a.systemId === b.systemId && a.comp.id === b.comp.id) : -1;
    if (i < 0) i = prev.findIndex((a, k) => !used.has(k) && a.systemId === b.systemId && key(a.comp.desc) === key(b.comp.desc));
    if (i > -1) used.add(i);
    out.push({ a: i > -1 ? prev[i] : null, b });
  });
  prev.forEach((a, k) => { if (!used.has(k)) out.push({ a, b: null }); });
  return out;
}

const packCost = (c: Component | null | undefined) => (c && c.pack && c.pack.cost != null ? num(c.pack.cost) : null);

/** the entries one card's change produces: changed $/unit or pack price, a new priced line, a removed line */
export function diffCardPrices(prev: RateCard | null | undefined, next: RateCard | null | undefined, meta: { jobType: string; source: PriceSource; at?: number; file?: string }): PriceEntry[] {
  if (!prev || !next) return [];   // a card's arrival or removal is not a price change
  const at = meta.at || Date.now();
  const out: PriceEntry[] = [];
  pair(slotsOf(prev), slotsOf(next)).forEach(({ a, b }) => {
    const from = a ? num(a.comp.cost) : null, to = b ? num(b.comp.cost) : null;
    const pf = packCost(a && a.comp), pt = packCost(b && b.comp);
    if (!b) { if (from != null && from > 0) out.push(entry(a as Slot, null, from, null, pf, null)); return; }
    if (!a) { if (to != null && to > 0) out.push(entry(null, b, null, to, null, pt)); return; }
    const costMoved = (from || 0) !== (to || 0), packMoved = (pf || 0) !== (pt || 0);
    if (!costMoved && !packMoved) return;
    out.push(entry(a, b, from, to, packMoved ? pf : undefined, packMoved ? pt : undefined));
  });
  return out;
  function entry(a: Slot | null, b: Slot | null, from: number | null, to: number | null, pf?: number | null, pt?: number | null): PriceEntry {
    const s = (b || a) as Slot;
    const e: PriceEntry = { at, jobType: meta.jobType, systemId: s.systemId, desc: String(s.comp.desc || ""), from, to, pct: from && to != null ? r2(((to - from) / from) * 100) : null, source: meta.source };
    if (s.comp.id) e.id = String(s.comp.id);
    if (s.comp.sku) e.sku = String(s.comp.sku);
    if (s.comp.unit) e.unit = String(s.comp.unit);
    if (pf !== undefined || pt !== undefined) { e.packFrom = pf == null ? null : pf; e.packTo = pt == null ? null : pt; }
    if (meta.file) e.file = meta.file;
    return e;
  }
}

/** every card at once (the import touches several): job type → card, before and after */
export function diffCardsPrices(prevCards: Record<string, any> | null | undefined, nextCards: Record<string, any> | null | undefined, meta: { source: PriceSource; at?: number; file?: string }): PriceEntry[] {
  const at = meta.at || Date.now();
  const p = prevCards || {}, n = nextCards || {};
  const out: PriceEntry[] = [];
  Object.keys(n).forEach((jt) => { if (p[jt] && n[jt] && p[jt] !== n[jt]) out.push(...diffCardPrices(p[jt], n[jt], { jobType: jt, source: meta.source, at, file: meta.file })); });
  return out;
}

/** newest first, capped — the settings blob carries it */
export function appendPriceHistory(history: any, entries: PriceEntry[], cap = PRICE_HISTORY_CAP): PriceEntry[] {
  const have = Array.isArray(history) ? history.filter((e) => e && typeof e === "object") : [];
  if (!entries || !entries.length) return have;
  return [...entries.slice().sort((x, y) => y.at - x.at), ...have].slice(0, Math.max(1, cap));
}

/** the trail for one card, or one component on it (by id, else by name within the finish) */
export function historyFor(history: any, q: { jobType?: string; systemId?: string | null; id?: string; desc?: string } = {}): PriceEntry[] {
  const list: PriceEntry[] = Array.isArray(history) ? history.filter((e) => e && typeof e === "object") : [];
  return list.filter((e) => {
    if (q.jobType && e.jobType !== q.jobType) return false;
    if (q.systemId !== undefined && (e.systemId || null) !== (q.systemId || null)) return false;
    if (q.id) return e.id === q.id || (!e.id && q.desc != null && key(e.desc) === key(q.desc));
    if (q.desc != null) return key(e.desc) === key(q.desc);
    return true;
  });
}
export function lastChangeFor(history: any, q: { jobType: string; systemId?: string | null; id?: string; desc?: string }): PriceEntry | null {
  return historyFor(history, q)[0] || null;
}

/** "$2.90 → $3.10 (+6.9%)" / "new at $3.10" / "removed (was $2.90)" */
export function describeChange(e: PriceEntry): string {
  const m = (n: number | null) => (n == null ? "—" : "$" + (Math.abs(n) >= 100 ? Math.round(n).toLocaleString("en-US") : n.toFixed(2)));
  if (e.from == null && e.to != null) return `new at ${m(e.to)}`;
  if (e.to == null) return `removed (was ${m(e.from)})`;
  if (e.from === e.to && e.packFrom !== undefined) return `pack ${m(e.packFrom == null ? null : e.packFrom)} → ${m(e.packTo == null ? null : e.packTo)}`;
  return `${m(e.from)} → ${m(e.to)}${e.pct != null ? ` (${e.pct > 0 ? "+" : ""}${e.pct}%)` : ""}`;
}
export const SOURCE_LABELS: Record<PriceSource, string> = { editor: "rate card", import: "price list", jobcost: "job cost sheet" };
